import { type FC, useState } from 'react';
import { Button, Form } from 'react-bootstrap';

import ModalWindow from '@editor2/components/ModalWindow';
import { showErrorToast } from '@editor2/services/toastService';
import { validateScoreConfig } from '@editor2/services/goReactValidationsService';

import { scoreSettingsContainer } from './styles';
import { type ScoreSettingsConfig } from '@soomo/lib/components/pageElements/GoReact/types';

interface Props {
	scoreSettings: ScoreSettingsConfig;
	isShown: boolean;
	onHide: () => void;
	onChange: (_: { score_config: ScoreSettingsConfig }) => void;
}

const ScoreSettingsModal: FC<Props> = (props) => {
	const { scoreSettings, isShown, onHide, onChange } = props;

	const [currentScoreConfig, setCurrentScoreConfig] = useState<ScoreSettingsConfig>(scoreSettings);

	const updateConfig = (value: Partial<ScoreSettingsConfig>) => {
		const updatedConfig = { ...currentScoreConfig, ...value };
		setCurrentScoreConfig(updatedConfig);
	};

	const isPointsForCompletion = currentScoreConfig?.scoreOptions === 'pointsForCompletion';

	const onUpdateSettingsClick = () => {
		try {
			validateScoreConfig(currentScoreConfig);

			onChange({ score_config: currentScoreConfig });
			onHide();
		} catch (error) {
			showErrorToast(error.message);
		}
	};

	const title = <div>Scoring settings</div>;

	const footer = (
		<Button variant="success" size="sm" onClick={onUpdateSettingsClick}>
			Update settings
		</Button>
	);

	return (
		<ModalWindow isShown={isShown} onHide={onHide} title={title} footer={footer}>
			<div className={scoreSettingsContainer}>
				<Form.Group>
					<Form.Label>Choose whether the assignment will receive points:</Form.Label>
					<Form.Check
						type="radio"
						checked={currentScoreConfig?.scoreOptions === 'noPoints'}
						onChange={() => setCurrentScoreConfig({ scoreOptions: 'noPoints' })}
						label="Presentational only (no required student interaction)"
					/>
					<Form.Check
						type="radio"
						checked={currentScoreConfig?.scoreOptions === 'instructorGrade'}
						onChange={() => setCurrentScoreConfig({ scoreOptions: 'instructorGrade' })}
						label="Graded by an instructor (within GoReact)"
					/>
					<Form.Check
						type="radio"
						checked={currentScoreConfig?.scoreOptions === 'pointsForCompletion'}
						onChange={() => setCurrentScoreConfig({ scoreOptions: 'pointsForCompletion' })}
						label="Points for completion"
					/>
				</Form.Group>

				{currentScoreConfig?.scoreOptions === 'pointsForCompletion' && (
					<Form.Group>
						<Form.Label>Completion options:</Form.Label>
						<Form.Check
							type="radio"
							disabled={!isPointsForCompletion}
							checked={!!currentScoreConfig?.completionOptions?.recordingMaking?.required}
							onChange={() =>
								updateConfig({ completionOptions: { recordingMaking: { required: true } } })
							}
							label="Get points for posting a recording"
						/>

						<Form.Check
							type="radio"
							disabled={!isPointsForCompletion}
							checked={!!currentScoreConfig?.completionOptions?.selfReview?.required}
							onChange={() =>
								updateConfig({
									completionOptions: {
										selfReview: { required: true, commentsNumber: 1, markersNumber: 1 }
									}
								})
							}
							label="Get points for reviewing your own recording"
						/>
						<Form.Group className="subBlock">
							<div className="numberInputWrapper">
								<Form.Control
									type="number"
									disabled={!currentScoreConfig?.completionOptions?.selfReview?.required}
									min={0}
									step={1}
									value={currentScoreConfig?.completionOptions?.selfReview?.commentsNumber || 0}
									onChange={(e) =>
										updateConfig({
											completionOptions: {
												selfReview: {
													...currentScoreConfig.completionOptions.selfReview,
													commentsNumber: Number(e.target.value)
												}
											}
										})
									}
								/>
								<Form.Text>
									min number of <strong>comments</strong> required
								</Form.Text>
							</div>

							<div className="numberInputWrapper">
								<Form.Control
									type="number"
									disabled={!currentScoreConfig?.completionOptions?.selfReview?.required}
									min={0}
									step={1}
									value={currentScoreConfig?.completionOptions?.selfReview?.markersNumber || 0}
									onChange={(e) =>
										updateConfig({
											completionOptions: {
												selfReview: {
													...currentScoreConfig.completionOptions.selfReview,
													markersNumber: Number(e.target.value)
												}
											}
										})
									}
								/>
								<Form.Text>
									min number of <strong>markers</strong> required
								</Form.Text>
							</div>

							<Form.Check
								type="checkbox"
								disabled={!currentScoreConfig?.completionOptions?.selfReview?.required}
								checked={currentScoreConfig?.completionOptions?.selfReview?.rubricSubmission}
								onChange={() =>
									updateConfig({
										completionOptions: {
											selfReview: {
												...currentScoreConfig.completionOptions.selfReview,
												rubricSubmission:
													!currentScoreConfig.completionOptions.selfReview.rubricSubmission
											}
										}
									})
								}
								label="rubric submission required"
							/>
						</Form.Group>

						<Form.Check
							type="radio"
							disabled={!isPointsForCompletion}
							checked={!!currentScoreConfig?.completionOptions?.nonSelfReview?.required}
							onChange={() =>
								updateConfig({
									completionOptions: {
										nonSelfReview: {
											required: true,
											reviewsNumber: 1,
											commentsNumber: 1,
											markersNumber: 1
										}
									}
								})
							}
							label="Get points for reviewing someone else's video"
						/>
						<Form.Group className="subBlock">
							<div className="numberInputWrapper">
								<Form.Control
									type="number"
									disabled={!currentScoreConfig?.completionOptions?.nonSelfReview?.required}
									min={0}
									step={1}
									value={currentScoreConfig?.completionOptions?.nonSelfReview?.reviewsNumber || 0}
									onChange={(e) =>
										updateConfig({
											completionOptions: {
												nonSelfReview: {
													...currentScoreConfig.completionOptions.nonSelfReview,
													reviewsNumber: Number(e.target.value)
												}
											}
										})
									}
								/>
								<Form.Text>
									min number of <strong>recordings to review</strong> required
								</Form.Text>
							</div>
							<div className="numberInputWrapper">
								<Form.Control
									type="number"
									disabled={!currentScoreConfig?.completionOptions?.nonSelfReview?.required}
									min={0}
									step={1}
									value={currentScoreConfig?.completionOptions?.nonSelfReview?.commentsNumber || 0}
									onChange={(e) =>
										updateConfig({
											completionOptions: {
												nonSelfReview: {
													...currentScoreConfig.completionOptions.nonSelfReview,
													commentsNumber: Number(e.target.value)
												}
											}
										})
									}
								/>
								<Form.Text>
									min number of <strong>comments</strong> required <strong>per review</strong>
								</Form.Text>
							</div>

							<div className="numberInputWrapper">
								<Form.Control
									type="number"
									disabled={!currentScoreConfig?.completionOptions?.nonSelfReview?.required}
									min={0}
									step={1}
									value={currentScoreConfig?.completionOptions?.nonSelfReview?.markersNumber || 0}
									onChange={(e) =>
										updateConfig({
											completionOptions: {
												nonSelfReview: {
													...currentScoreConfig.completionOptions.nonSelfReview,
													markersNumber: Number(e.target.value)
												}
											}
										})
									}
								/>
								<Form.Text>
									min number of <strong>markers</strong> required <strong>per review</strong>
								</Form.Text>
							</div>

							<Form.Check
								type="checkbox"
								disabled={!currentScoreConfig?.completionOptions?.nonSelfReview?.required}
								checked={currentScoreConfig?.completionOptions?.nonSelfReview?.rubricSubmission}
								onChange={() =>
									updateConfig({
										completionOptions: {
											nonSelfReview: {
												...currentScoreConfig.completionOptions.nonSelfReview,
												rubricSubmission:
													!currentScoreConfig.completionOptions.nonSelfReview.rubricSubmission
											}
										}
									})
								}
								label="rubric submission required"
							/>
						</Form.Group>

						<Form.Check
							type="radio"
							disabled={!isPointsForCompletion}
							checked={!!currentScoreConfig?.completionOptions?.providedReview?.required}
							onChange={() =>
								updateConfig({
									completionOptions: {
										providedReview: { required: true, commentsNumber: 1, markersNumber: 1 }
									}
								})
							}
							label="Get points for reviewing provided video"
						/>
						<Form.Group className="subBlock">
							<div className="numberInputWrapper">
								<Form.Control
									type="number"
									disabled={!currentScoreConfig?.completionOptions?.providedReview?.required}
									min={0}
									step={1}
									value={currentScoreConfig?.completionOptions?.providedReview?.commentsNumber || 0}
									onChange={(e) =>
										updateConfig({
											completionOptions: {
												providedReview: {
													...currentScoreConfig.completionOptions.providedReview,
													commentsNumber: Number(e.target.value)
												}
											}
										})
									}
								/>
								<Form.Text>
									min number of <strong>comments</strong> required
								</Form.Text>
							</div>

							<div className="numberInputWrapper">
								<Form.Control
									type="number"
									disabled={!currentScoreConfig?.completionOptions?.providedReview?.required}
									min={0}
									step={1}
									value={currentScoreConfig?.completionOptions?.providedReview?.markersNumber || 0}
									onChange={(e) =>
										updateConfig({
											completionOptions: {
												providedReview: {
													...currentScoreConfig.completionOptions.providedReview,
													markersNumber: Number(e.target.value)
												}
											}
										})
									}
								/>
								<Form.Text>
									min number of <strong>markers</strong> required
								</Form.Text>
							</div>

							<Form.Check
								type="checkbox"
								disabled={!currentScoreConfig?.completionOptions?.providedReview?.required}
								checked={currentScoreConfig?.completionOptions?.providedReview?.rubricSubmission}
								onChange={() =>
									updateConfig({
										completionOptions: {
											providedReview: {
												...currentScoreConfig.completionOptions.providedReview,
												rubricSubmission:
													!currentScoreConfig.completionOptions.providedReview.rubricSubmission
											}
										}
									})
								}
								label="rubric submission required"
							/>
						</Form.Group>
					</Form.Group>
				)}
			</div>
		</ModalWindow>
	);
};

export default ScoreSettingsModal;
